'use client'

import * as React from 'react'
import { Box, cn } from '@hanzo/ui'
import type { ChildMenu, LinkDefExtended } from '../../site-def/main-nav'
import { anchor } from '../../util/anchor'

/**
 * Hover-opened menu, the way apple.com does it: a slim bar, plain text
 * items, and a panel that drops the full width of the bar with the item's
 * children set in columns, one column per `groupName`.
 *
 * The panel is `absolute` against the `fixed` header, so it needs no
 * portal and never fights the page for z-order. Open state is one index.
 * Leaving the bar or the panel closes it after a short grace so a cursor
 * crossing the gap between item and panel does not flicker it shut.
 */
const DesktopNav: React.FC<{ links: LinkDefExtended[] }> = ({ links }) => {
  const [open, setOpen] = React.useState<number | null>(null)
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const hold = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null } }
  const show = (i: number) => { hold(); setOpen(i) }
  const hide = () => { hold(); timer.current = setTimeout(() => setOpen(null), 140) }

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(null) }
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('keydown', onKey); hold() }
  }, [])

  // The panel keeps showing the last item through its fade instead of emptying
  // the instant the cursor leaves.
  const last = React.useRef<LinkDefExtended | undefined>(undefined)
  if (open !== null) last.current = links[open]
  const current = last.current
  const panelOpen = open !== null && !!current?.childMenu?.length

  return links.length > 0 ? (
    <>
      <Box tag='nav' aria-label='Primary' className='flex flex-row items-center gap-1' onMouseLeave={hide}>
        {links.map((el, i) => {
          const hasChildren = !!el.childMenu?.length
          return (
            <Box
              tag='a'
              key={i}
              href={el.href || '/'}
              {...anchor(el)}
              aria-haspopup={hasChildren ? 'true' : undefined}
              aria-expanded={hasChildren ? open === i : undefined}
              onMouseEnter={() => (hasChildren ? show(i) : hide())}
              onFocus={() => (hasChildren ? show(i) : hide())}
              onBlur={hide}
              className={cn(
                'px-3 py-1 text-xs leading-none whitespace-nowrap no-underline transition duration-150',
                open === i ? 'text-white' : 'text-neutral-300 hover:text-white'
              )}
            >
              {el.title}
            </Box>
          )
        })}
      </Box>

      {/* Scrim: the page recedes while a menu is open. Sits under the bar. */}
      <Box
        aria-hidden
        onMouseEnter={hide}
        className={cn(
          'fixed left-0 right-0 top-12 bottom-0 bg-black/50 backdrop-blur-sm transition duration-200',
          panelOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      />

      <Box
        data-panel='nav'
        aria-hidden={!panelOpen}
        onMouseEnter={hold}
        onMouseLeave={hide}
        onBlur={hide}
        className={cn(
          'absolute left-0 right-0 top-12 bg-black/95 backdrop-blur-2xl',
          'border-b border-white/[0.08] shadow-2xl',
          'transition duration-200',
          panelOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1 pointer-events-none'
        )}
      >
        {current?.childMenu && (
          <Box className='max-w-[1024px] mx-auto px-6 py-10 flex flex-row gap-14'>
            {Columns(current.childMenu, hide)}
          </Box>
        )}
      </Box>
    </>
  ) : null
}

export default DesktopNav

const Columns = (childs: ChildMenu[], close: () => void) => {
  const groups = childs.reduce<Record<string, ChildMenu[]>>((acc, c) => {
    const k = c.groupName ?? ''
    ;(acc[k] ??= []).push(c)
    return acc
  }, {})

  return Object.entries(groups).map(([name, items]) => (
    <Box key={name} className='min-w-[160px]'>
      {name && (
        <Box className='text-xs tracking-wide text-neutral-500 mb-3'>{name}</Box>
      )}
      <Box tag='ul' className='flex flex-col gap-2.5 m-0 p-0'>
        {items.map((it) => (
          <Box tag='li' key={it.title} className='block'>
            <Box
              tag='a'
              href={it.href}
              {...anchor(it)}
              onClick={close}
              className='flex flex-row items-start gap-3 no-underline text-neutral-200 hover:text-white'
            >
              {it.icon && <Box className='shrink-0 mt-0.5'>{it.icon}</Box>}
              <Box>
              <Box className='text-[17px] font-semibold leading-snug'>
                {it.title}
              </Box>
              {it.details && (
                <Box className='text-xs leading-snug text-neutral-500'>
                  {it.details}
                </Box>
              )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  ))
}
